import { Fragment, useRef } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { LuDog, LuBuilding2, LuUser } from "react-icons/lu";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { ICartDetailsResponseData, ICartHdCartItem } from "@/api_framework/api_modals/Cart";

interface CartDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onContinue?: () => void;
  customerName: string;
  activeCartDetails: ICartDetailsResponseData | null;
  hdItems: ICartHdCartItem[];
  // Referral Reward credit; used on clinic bills at payment if left on.
  rewardCreditBalance?: number;
  useRewardCredit?: boolean;
  onUseRewardCreditChange?: (value: boolean) => void;
}

const formatAmount = (amount: number | string | undefined | null) =>
  `₹${Number(amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CartDetailsModal = ({
  isOpen,
  onClose,
  onContinue,
  customerName,
  activeCartDetails,
  hdItems,
  rewardCreditBalance = 0,
  useRewardCredit = true,
  onUseRewardCreditChange,
}: CartDetailsModalProps) => {
  const cancelButtonRef = useRef(null);
  const mappedClinicName = useAppSelector((state) => state.root.UIStates.mappedClinicName);
  const priceBreakup = activeCartDetails?.price_breakup;
  const hasHdItems = hdItems.length > 0;

  // Estimate only: home-delivery items are billed separately and can't use Reward credit.
  const clinicTotal = Math.max(
    0,
    Number(priceBreakup?.net_total_after_tax || 0) - (hasHdItems ? Number(priceBreakup?.home_delivery_total || 0) : 0),
  );
  const rewardCreditApplied =
    useRewardCredit && onUseRewardCreditChange ? Math.min(Number(rewardCreditBalance) || 0, clinicTotal) : 0;

  const petsWithItems = (activeCartDetails?.pets ?? []).filter((pet) => pet?.items?.length > 0);
  const petNames = petsWithItems.map((pet) => pet.pet_name).filter(Boolean);
  const lineItemCount = petsWithItems.reduce((count, pet) => count + pet.items.length, 0) + hdItems.length;

  const summaryRows = [
    { label: "Total MRP", value: formatAmount(priceBreakup?.total_gross_price) },
    { label: "Discount", value: `- ${formatAmount(priceBreakup?.total_discount)}` },
    ...(Number(priceBreakup?.total_cart_level_discount) > 0
      ? [{ label: "Cart Discount", value: `- ${formatAmount(priceBreakup?.total_cart_level_discount)}` }]
      : []),
    ...(hasHdItems ? [{ label: "Delivery Charges", value: formatAmount(priceBreakup?.delivery_charges) }] : []),
    { label: "Total Tax", value: formatAmount(priceBreakup?.total_tax) },
  ];

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-40" initialFocus={cancelButtonRef} onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative flex max-h-[90vh] w-full transform flex-col overflow-hidden rounded-xl bg-white text-left shadow-xl transition-all sm:my-8 sm:max-w-lg">
                <div className="flex items-start justify-between px-6 pb-4 pt-5">
                  <div>
                    <Dialog.Title className="text-base font-semibold text-gray-900">
                      Review cart before checkout
                    </Dialog.Title>
                    <p className="mt-0.5 text-xs text-gray-500">
                      Please verify these details with the pet parent to avoid invoice cancellation later.
                    </p>
                    {mappedClinicName && (
                      <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">
                        <LuBuilding2 className="h-3.5 w-3.5 text-gray-500" />
                        <span className="text-gray-500">Clinic :</span>
                        {mappedClinicName}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    className="-mr-1 rounded-md p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="grid grid-cols-2 divide-x divide-gray-200 border-y border-gray-200 bg-gray-50">
                  <div className="flex items-center gap-3 px-6 py-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#eff1ff] text-[#3969ff]">
                      <LuUser className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[11px] uppercase tracking-wide text-gray-500">Customer</p>
                      <p className="truncate text-sm font-medium text-gray-900" title={customerName}>
                        {customerName || "-"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 px-6 py-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#f8e7d6] text-[#a75610]">
                      <LuDog className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[11px] uppercase tracking-wide text-gray-500">
                        {petNames.length > 1 ? "Pets" : "Pet"}
                      </p>
                      <p className="truncate text-sm font-medium text-gray-900" title={petNames.join(", ")}>
                        {petNames.length > 0 ? petNames.join(", ") : "-"}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
                  <div className="mb-3 flex items-center justify-between">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Price Breakup</p>
                    <span className="rounded-full bg-[#e7f8ef] px-3 py-1 text-xs font-medium text-[#07863d] ring-1 ring-[#07863d]/20">
                      <span className="font-bold">{lineItemCount}</span>{" "}
                      {lineItemCount === 1 ? "Line Item" : "Line Items"}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {summaryRows.map((row) => (
                      <div key={row.label} className="flex justify-between text-sm">
                        <span className="text-gray-500">{row.label}</span>
                        <span className="text-gray-700">{row.value}</span>
                      </div>
                    ))}
                    {hasHdItems && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Home Delivery Total</span>
                        <span className="text-gray-700">{formatAmount(priceBreakup?.home_delivery_total)}</span>
                      </div>
                    )}
                  </div>
                  {rewardCreditBalance > 0 && onUseRewardCreditChange && (
                    <label className="mt-4 flex cursor-pointer items-center justify-between rounded-lg border border-[#07863d]/30 bg-[#e7f8ef] px-3 py-2.5">
                      <span>
                        <span className="block text-sm font-medium text-gray-900">Use Reward credit</span>
                        <span className="block text-xs text-gray-600">
                          {formatAmount(rewardCreditBalance)} available · clinic bills only, applied at payment
                        </span>
                      </span>
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-[#07863d]"
                        checked={useRewardCredit}
                        onChange={(e) => onUseRewardCreditChange(e.target.checked)}
                      />
                    </label>
                  )}
                </div>

                <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
                  <div className={`flex items-baseline justify-between ${onContinue && !rewardCreditApplied ? "mb-4" : ""}`}>
                    <span className="text-sm font-medium text-gray-700">Invoice Amount</span>
                    <span className="text-xl font-semibold text-gray-900">
                      {formatAmount(priceBreakup?.net_total_after_tax)}
                    </span>
                  </div>
                  {rewardCreditApplied > 0 && (
                    <div className={onContinue ? "mb-4" : ""}>
                      <div className="mt-1 flex justify-between text-sm">
                        <span className="text-[#07863d]">Reward credit</span>
                        <span className="text-[#07863d]">- {formatAmount(rewardCreditApplied)}</span>
                      </div>
                      <div className="mt-1 flex items-baseline justify-between">
                        <span className="text-sm font-medium text-gray-700">Amount to Pay</span>
                        <span className="text-xl font-semibold text-gray-900">
                          {formatAmount(Number(priceBreakup?.net_total_after_tax || 0) - rewardCreditApplied)}
                        </span>
                      </div>
                    </div>
                  )}
                  {onContinue && (
                  <div className="flex gap-3">
                    <button
                      type="button"
                      ref={cancelButtonRef}
                      onClick={onClose}
                      className="flex-1 rounded-lg border border-gray-300 bg-white py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-100"
                    >
                      Go Back
                    </button>
                    <button
                      type="button"
                      onClick={onContinue}
                      className="flex-1 rounded-lg bg-[#07863d] py-2.5 text-sm font-medium text-white transition hover:bg-[#066e32]"
                    >
                      Continue
                    </button>
                  </div>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default CartDetailsModal;
